import { useState, useEffect, useRef } from 'react'
import { Outlet } from 'react-router'
import s from './Leads.scss'
import { PageTitle } from 'components/common/PageTitle'
import { SwitchToggle, RangeDatePicker } from 'components/ideal-core-ui'
import { COOKIES } from 'utils/constants'
import Cookies from 'universal-cookie'
import { utcDate } from 'utils/helpers'
import moment from 'moment'
import { AddButton } from 'components/common/AddButton'
import { FaPlusCircle, FaFileExcel } from 'react-icons/fa'
import { AddLeadModal } from './AddLeadModal'
import {
  makeLeadsPostRequest,
  makeLeadsDeleteRequest,
  makeLeadsPatchRequest,
  getLeadsBaseUrl,
  makeLeadsGetRequest
} from 'utils/api'
import { useLoading, useFirstRender } from 'hooks'
import { fromLead, toEmailContacts } from './serializers'
import { List } from './List'
import { Button } from 'reactstrap'
import swal from 'sweetalert'
import { EmailContactModalContainer } from 'components/common/modals/EmailContactModalContainer'
import { useLocation } from 'react-router-dom'
import { getToken } from 'utils/authService'
import Select from 'react-select'

const cookies = new Cookies()

const Leads = () => {
  const tableRef = useRef(null)
  const firstRender = useFirstRender()
  const location = useLocation()

  const [, withLoadingLocks] = useLoading()
  const [startDate, setStartDate] = useState('')
  const [startDateEnabled, setStartDateEnabled] = useState(false)
  const [endDate, setEndDate] = useState('')
  const [startFollowupDate, setStartFollowupDate] = useState('')
  const [startFollowupDateEnabled, setStartFollowupDateEnabled] = useState(false)
  const [endFollowupDate, setEndFollowupDate] = useState('')
  const [startLastTouchedDate, setStartLastTouchedDate] = useState('')
  const [startLastTouchedDateEnabled, setStartLastTouchedDateEnabled] = useState(false)
  const [endLastTouchedDate, setEndLastTouchedDate] = useState('')
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false)
  const [isArchived, setIsArchived] = useState(false)
  const [selectedLeads, setSelectedLeads] = useState([])
  const [showEmailContactModal, setShowEmailContactModal] = useState(false)
  const [creators, setCreators] = useState([])
  const [filteredByUser, setFilteredByUser] = useState(null)

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  const setFollowupDateFilters = (dates, utc = true) => {
    setStartFollowupDate(utc ? utcDate(dates.from) : dates.from)
    setEndFollowupDate(utc ? utcDate(dates.to) : dates.to)
  }

  const setLastTouchedDateFilters = (dates, utc = true) => {
    setStartLastTouchedDate(utc ? utcDate(dates.from) : dates.from)
    setEndLastTouchedDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    if (firstRender) return
    tableRef.current && tableRef.current.onQueryChange()
    setSelectedLeads([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateEnabled,
    startFollowupDateEnabled,
    startLastTouchedDateEnabled,
    startDate,
    startFollowupDate,
    startLastTouchedDate,
    endDate,
    endFollowupDate,
    endLastTouchedDate,
    isArchived,
    filteredByUser
  ])

  useEffect(() => {
    if (location.state && location.state.fromLead) {
      tableRef.current && tableRef.current.onQueryChange()
    }
  }, [location])

  useEffect(() => {
    const fetchCreators = async () => {
      const { emails } = await makeLeadsGetRequest('/v1/leads/creators')
      setCreators(emails.map(creator => ({ value: creator, label: creator })))
    }
    fetchCreators()
  }, [])

  useEffect(() => {
    const sessionDateRangeFilters = cookies.get(COOKIES.DATE_RANGE_FILTERS)
    if (sessionDateRangeFilters?.startDate) {
      setDateFilters(
        {
          from: sessionDateRangeFilters.startDate,
          to: sessionDateRangeFilters.endDate
        },
        false
      )
    } else {
      setDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }

    const sessionFollowupDateRangeFilters = cookies.get(COOKIES.FOLLOWUP_DATE_RANGE_FILTERS)
    if (sessionFollowupDateRangeFilters?.startDate) {
      setFollowupDateFilters(
        {
          from: sessionFollowupDateRangeFilters.startDate,
          to: sessionFollowupDateRangeFilters.endDate
        },
        false
      )
    } else {
      setFollowupDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }

    const sessionLastTouchedDateRangeFilters = cookies.get(COOKIES.LAST_TOUCHED_DATE_RANGE_FILTERS)
    if (sessionLastTouchedDateRangeFilters?.startDate) {
      setLastTouchedDateFilters(
        {
          from: sessionLastTouchedDateRangeFilters.startDate,
          to: sessionLastTouchedDateRangeFilters.endDate
        },
        false
      )
    } else {
      setLastTouchedDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }
  }, [])

  const toggleAddLeadModal = () => {
    setAddLeadModalOpen(!addLeadModalOpen)
  }

  const createLead = async lead => {
    await withLoadingLocks(makeLeadsPostRequest('/v1/leads', fromLead(lead)))
    tableRef.current && tableRef.current.onQueryChange()
    setSelectedLeads([])
  }

  const toggleArchiveLead = async (id, isArchived) => {
    await withLoadingLocks(
      makeLeadsPatchRequest(`/v1/leads/${id}`, {
        isArchived: !isArchived
      })
    )
    tableRef.current && tableRef.current.onQueryChange()
    setSelectedLeads([])
  }

  const deleteLead = async id => {
    const willRemove = await swal({
      title: 'Are you sure you want to delete this lead?',
      text: 'This action can’t be undone',
      buttons: ['Cancel', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      await withLoadingLocks(makeLeadsDeleteRequest(`/v1/leads/${id}`))
      tableRef.current && tableRef.current.onQueryChange()
      setSelectedLeads([])
    }
  }

  const archiveLeads = async () => {
    const willArchive = await swal({
      title: 'Are you sure you want to archive these leads?',
      buttons: ['Cancel', 'Yes'],
      icon: 'warning'
    })
    if (willArchive) {
      await withLoadingLocks(
        makeLeadsPatchRequest('/v1/leads/archiveByIdList', {
          leadIds: selectedLeads.map(lead => lead.id)
        })
      )
      tableRef.current && tableRef.current.onQueryChange()
      setSelectedLeads([])
    }
  }

  const toggleEmailContactModal = () => setShowEmailContactModal(prev => !prev)

  const onBtnExport = () => {
    window.open(
      getLeadsBaseUrl(
        `/v1/leads/export?token=${getToken()}${
          startDateEnabled ? '&createdStartDate=' + startDate + '&createdEndDate=' + endDate : ''
        }${
          startFollowupDateEnabled
            ? '&followupStartDate=' + startFollowupDate + '&followupEndDate=' + endFollowupDate
            : ''
        }${
          startLastTouchedDateEnabled
            ? '&lastTouchedStartDate=' +
              startLastTouchedDate +
              '&lastTouchedEndDate=' +
              endLastTouchedDate
            : ''
        }${isArchived ? '&isArchived=true' : ''}&creators=${filteredByUser?.value ?? ''}`
      ),
      '_blank'
    )
  }

  const toggleEmailLead = lead => {
    setSelectedLeads([lead])
    toggleEmailContactModal()
  }

  return (
    <>
      <PageTitle view="Leads" />
      <div className={s.container}>
        <div className={s.filters}>
          <div className={s.filter}>
            <Select
              onChange={value => {
                setFilteredByUser(value)
              }}
              options={creators}
              value={filteredByUser}
              className={s.userSelect}
              isClearable
            />
          </div>
          <div className={s.filter}>
            <SwitchToggle noText onToggle={setStartDateEnabled}>
              Date Entered
            </SwitchToggle>
            <RangeDatePicker
              startDate={startDate}
              endDate={endDate}
              onChange={setDateFilters}
              disabled={!startDateEnabled}
            />
          </div>
          <div className={s.filter}>
            <SwitchToggle noText onToggle={setStartFollowupDateEnabled}>
              Follow-up Date
            </SwitchToggle>
            <RangeDatePicker
              startDate={startFollowupDate}
              endDate={endFollowupDate}
              onChange={setFollowupDateFilters}
              disabled={!startFollowupDateEnabled}
            />
          </div>
          <div className={s.filter}>
            <SwitchToggle noText onToggle={setStartLastTouchedDateEnabled}>
              Last Touched
            </SwitchToggle>
            <RangeDatePicker
              startDate={startLastTouchedDate}
              endDate={endLastTouchedDate}
              onChange={setLastTouchedDateFilters}
              disabled={!startLastTouchedDateEnabled}
            />
          </div>
          <div className={s.archivedFilter}>
            <span onClick={() => setIsArchived(!isArchived)}>
              {isArchived ? 'View Active Leads' : 'View Archived Leads'}
            </span>
          </div>
          <div className={s.clearfix} />
        </div>
        <div className={s.table}>
          <AddButton className={s.addLeadBtn} onClick={toggleAddLeadModal}>
            <FaPlusCircle />
            Add Lead
          </AddButton>
          <AddButton className={s.exportExcel} simpleBtn onClick={onBtnExport}>
            <FaFileExcel
              style={{ position: 'relative', bottom: 2, right: 5 }}
              className={s.excelIcon}
            />
            Export to Excel
          </AddButton>
          {selectedLeads.length > 0 && (
            <>
              <Button secondary className={s.archiveBtn} onClick={archiveLeads}>
                {isArchived ? 'Unarchive' : 'Archive'} Selected Leads
              </Button>
              {toEmailContacts(selectedLeads).length > 0 && (
                <Button className={s.emailBtn} onClick={toggleEmailContactModal}>
                  Email Selected Leads
                </Button>
              )}
              <div className={s.clearfix} />
            </>
          )}
          <List
            tableRef={tableRef}
            startDateEnabled={startDateEnabled}
            startDate={startDate}
            filteredByUser={filteredByUser}
            endDate={endDate}
            startFollowupDateEnabled={startFollowupDateEnabled}
            startFollowupDate={startFollowupDate}
            endFollowupDate={endFollowupDate}
            startLastTouchedDateEnabled={startLastTouchedDateEnabled}
            startLastTouchedDate={startLastTouchedDate}
            endLastTouchedDate={endLastTouchedDate}
            isArchived={isArchived}
            toggleArchiveLead={toggleArchiveLead}
            deleteLead={deleteLead}
            setSelectedLeads={setSelectedLeads}
            toggleEmailContactModal={toggleEmailContactModal}
            toggleEmailLead={toggleEmailLead}
          />
        </div>
        <Outlet />
      </div>
      {addLeadModalOpen && (
        <AddLeadModal
          toggleAddLeadModal={toggleAddLeadModal}
          addLeadModalOpen={addLeadModalOpen}
          createLead={createLead}
        />
      )}
      {showEmailContactModal && (
        <EmailContactModalContainer
          toggleEmailContactModal={toggleEmailContactModal}
          showEmailContactModal={showEmailContactModal}
          contact={toEmailContacts(selectedLeads)}
          app="leads"
          leadId={selectedLeads.length === 1 && selectedLeads[0].id}
        />
      )}
    </>
  )
}

export default Leads
